/**
 * Shared formatters for the builder chat — cost (USD) and token usage.
 * Used by both MessageList (per-turn footer) and BuilderChatPanel (session
 * total in the header).
 */

export const formatCost = (usd) => {
  if (typeof usd !== 'number' || usd <= 0) return null
  if (usd < 0.01) return `${(usd * 100).toFixed(2)}¢`
  return `$${usd.toFixed(3)}`
}

const compactNumber = (n) => {
  if (n < 1_000) return String(n)
  if (n < 10_000) return `${(n / 1_000).toFixed(1)}k`
  return `${Math.round(n / 1_000)}k`
}

/**
 * Per-message verbose breakdown.
 *   "245 out · 89 in · 4480 cached"
 */
export const formatTokens = (usage) => {
  if (!usage) return null
  const out = usage.output_tokens ?? 0
  const cached = usage.cache_read_input_tokens ?? 0
  const fresh = (usage.input_tokens ?? 0) + (usage.cache_creation_input_tokens ?? 0)
  return `${out} out · ${fresh} in · ${cached} cached`
}

/**
 * Aggregate session totals across every assistant message.
 * Returns { cost, output, fresh, cached, total } or null if nothing to show.
 */
export const aggregateUsage = (messages) => {
  let cost = 0
  let output = 0
  let cached = 0
  let fresh = 0
  let hasAny = false
  for (const m of messages) {
    if (m.role !== 'assistant') continue
    if (typeof m.cost === 'number') {
      cost += m.cost
      hasAny = true
    }
    if (m.usage) {
      output += m.usage.output_tokens ?? 0
      cached += m.usage.cache_read_input_tokens ?? 0
      fresh += (m.usage.input_tokens ?? 0) + (m.usage.cache_creation_input_tokens ?? 0)
      hasAny = true
    }
  }
  if (!hasAny) return null
  return { cost, output, cached, fresh, total: output + cached + fresh }
}

/**
 * Header summary — short, no labels: "$0.045 · 7.4k"
 * The breakdown lives in the tooltip via formatSessionDetail().
 */
export const formatSessionHeadline = (agg) => {
  if (!agg) return null
  const parts = []
  const c = formatCost(agg.cost)
  if (c) parts.push(c)
  if (agg.total > 0) parts.push(`${compactNumber(agg.total)} tokens`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Verbose tooltip with the full breakdown.
 */
export const formatSessionDetail = (agg) => {
  if (!agg) return null
  return [
    `Coût total : ${formatCost(agg.cost) ?? '—'}`,
    `Tokens output : ${agg.output.toLocaleString()}`,
    `Tokens input frais : ${agg.fresh.toLocaleString()}`,
    `Tokens cache read : ${agg.cached.toLocaleString()}`,
  ].join('\n')
}
